import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useOrganizationMembers } from '../hooks'
import { organizationsApi } from '../api'
import { usersApi } from '@/features/users/api'
import { useAuthentication } from '@/features/auth/AuthenticationContext'
import { AuthorizationGate } from '@/features/authorization/AuthorizationGate'
import { Users, Crown, Shield, User, UserPlus, Trash2, Search, ChevronLeft, ChevronRight } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'

interface OrganizationUser {
  id: string
  user_id: string
  user_name: string
  user_email: string
  role: string
  is_active: boolean
  joined_at: string
}

interface UserManagementProps {
  organizationId: string
}

export function UserManagement({ organizationId }: UserManagementProps) {
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [memberPage, setMemberPage] = useState(0)
  const MEMBERS_PER_PAGE = 5
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { user: currentUser } = useAuthentication()

  const { data: usersData, isLoading, error } = useOrganizationMembers(organizationId)
  const members: OrganizationUser[] = Array.isArray(usersData) ? usersData : usersData?.items || usersData?.data || usersData?.members || []

  // Fetch all system users for add dialog
  const { data: allUsersData } = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => usersApi.list().then(res => res.data),
    enabled: showAddDialog,
  })
  const allUsers = Array.isArray(allUsersData) ? allUsersData : allUsersData?.items || allUsersData?.data || []

  // Filter users: not already members, matching search
  const memberIds = new Set(members.map(m => m.user_id))
  const availableUsers = allUsers.filter((u: any) =>
    !memberIds.has(u.id) &&
    (u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  const addMemberMutation = useMutation({
    mutationFn: (userId: string) => organizationsApi.addMember(organizationId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations', organizationId, 'members'] })
      toast({ title: 'Member added', description: 'User has been added to the organization.' })
    },
    onError: (err: any) => {
      toast({
        title: 'Error',
        description: err.response?.data?.title || 'Failed to add member.',
        variant: 'destructive',
      })
    },
  })

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => organizationsApi.removeMember(organizationId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations', organizationId, 'members'] })
      toast({ title: 'Member removed', description: 'User has been removed from the organization.' })
    },
    onError: (err: any) => {
      toast({
        title: 'Error',
        description: err.response?.data?.title || 'Failed to remove member.',
        variant: 'destructive',
      })
    },
  })

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner': return <Crown className="h-3 w-3" />
      case 'admin': return <Shield className="h-3 w-3" />
      default: return <User className="h-3 w-3" />
    }
  }

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'owner':
        return <Badge className="bg-amber-100 text-amber-800 border-amber-300 flex items-center gap-1">{getRoleIcon(role)} Owner</Badge>
      case 'admin':
        return <Badge variant="secondary" className="flex items-center gap-1">{getRoleIcon(role)} Admin</Badge>
      default:
        return <Badge variant="outline" className="flex items-center gap-1">{getRoleIcon(role)} Member</Badge>
    }
  }

  const getInitials = (name: string) => {
    const parts = (name || '?').split(' ')
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0][0].toUpperCase()
  }

  const getAvatarColor = (name: string) => {
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500']
    let hash = 0
    for (const c of name || '') hash = c.charCodeAt(0) + ((hash << 5) - hash)
    return colors[Math.abs(hash) % colors.length]
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-32">
          <LoadingSpinner />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <p className="text-red-500">Failed to load members.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Members ({members.length})
            </CardTitle>
            <CardDescription>
              People with access to this organization
            </CardDescription>
          </div>
          <AuthorizationGate actions={['manage_members']}>
            <Button size="sm" onClick={() => setShowAddDialog(true)}>
              <UserPlus className="h-4 w-4 mr-1" />
              Add Members
            </Button>
          </AuthorizationGate>
        </CardHeader>
        <CardContent>
          {members.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No members yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {members.slice(memberPage * MEMBERS_PER_PAGE, (memberPage + 1) * MEMBERS_PER_PAGE).map((member) => {
                const isCurrentUser = currentUser?.id === member.user_id
                const isOwner = member.role === 'owner'
                return (
                  <div key={member.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`h-10 w-10 rounded-full ${getAvatarColor(member.user_name)} flex items-center justify-center flex-shrink-0`}>
                        <span className="text-sm font-medium text-white">
                          {getInitials(member.user_name)}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-medium text-sm">
                            {member.user_name || member.user_email || 'Unknown'}
                            {isCurrentUser && <span className="text-gray-400 font-normal ml-1">(You)</span>}
                          </h3>
                          {getRoleBadge(member.role)}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{member.user_email}</p>
                      </div>
                    </div>
                    {!isOwner && (
                      <AuthorizationGate actions={['manage_members']}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
                          onClick={() => {
                            if (window.confirm(`Remove "${member.user_name}" from this organization?`)) {
                              removeMemberMutation.mutate(member.user_id)
                            }
                          }}
                          disabled={removeMemberMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AuthorizationGate>
                    )}
                  </div>
                )
              })}
            </div>
          )}
          {members.length > MEMBERS_PER_PAGE && (
            <div className="flex items-center justify-between pt-4 border-t mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMemberPage(p => Math.max(0, p - 1))}
                disabled={memberPage === 0}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-gray-500">
                {memberPage * MEMBERS_PER_PAGE + 1}–{Math.min((memberPage + 1) * MEMBERS_PER_PAGE, members.length)} of {members.length}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMemberPage(p => p + 1)}
                disabled={(memberPage + 1) * MEMBERS_PER_PAGE >= members.length}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Members Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Add Members
            </DialogTitle>
            <DialogDescription>
              Select users to add to this organization
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="max-h-64 overflow-y-auto space-y-2">
            {availableUsers.length === 0 ? (
              <p className="text-center text-gray-500 py-4 text-sm">
                {searchQuery ? 'No matching users found.' : 'All users are already members.'}
              </p>
            ) : (
              availableUsers.map((user: any) => (
                <div key={user.id} className="flex items-center justify-between p-2 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`h-8 w-8 rounded-full ${getAvatarColor(user.name)} flex items-center justify-center flex-shrink-0`}>
                      <span className="text-xs font-medium text-white">
                        {getInitials(user.name || user.email)}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{user.name || user.email}</p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => addMemberMutation.mutate(user.id)}
                    disabled={addMemberMutation.isPending}
                  >
                    Add
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
